"use client";
import { useEffect, useRef, useState, type RefObject } from "react";
import type Hls from "hls.js";

export function useHlsPlayback(videoRef: RefObject<HTMLVideoElement | null>, url: string, enabled: boolean) {
  const instance = useRef<Hls | null>(null);
  const [levels, setLevels] = useState<Array<{ index: number; height: number }>>([]);
  const [quality, setQuality] = useState(-1);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url || !enabled) return;
    let disposed = false;
    function nativePlayback() {
      if (video!.canPlayType("application/vnd.apple.mpegurl")) { video!.src = url; video!.load(); }
      else setFailed(true);
    }
    void import("hls.js").then(({ default: Hls }) => {
        if (disposed) return;
        setFailed(false);
        if (!Hls.isSupported()) { nativePlayback(); return; }
        const hls = new Hls({ capLevelToPlayerSize: true, startLevel: -1, maxBufferLength: 30, maxMaxBufferLength: 60 });
        instance.current = hls;
        let recovered = false;
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered) { recovered = true; hls.recoverMediaError(); }
          else { hls.stopLoad(); setFailed(true); }
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLevels(hls.levels.map((level, index) => ({ index, height: level.height })));
          setQuality(-1);
        });
        hls.loadSource(url);
        hls.attachMedia(video);
      }).catch(() => { if (!disposed) nativePlayback(); });
    return () => { disposed = true; instance.current?.destroy(); instance.current = null; video.removeAttribute("src"); video.load(); };
  }, [enabled, url, videoRef, reload]);
  function selectQuality(index: number) {
    if (instance.current) instance.current.currentLevel = index;
    setQuality(index);
  }
  return { levels: enabled ? levels : [], quality, selectQuality, failed: enabled && failed, retry: () => setReload((value) => value + 1) };
}
